import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageSection, PageSectionVariants, EmptyState, EmptyStateBody, EmptyStateHeader } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { selectCustomizationBasesInNameOrder, selectVersionTagsForCustomizationBases } from '../../../store/contouring/contouringSelectors';
import { allCustomizationChangesReverted, contouringSelectors, modelCustomizationSaved, modelSelectionChangesReverted } from '../../../store/contouring/contouringSlice';
import { StoreState } from '../../../store/store';
import { configurationTargetSelectors } from '../../../store/configurationTarget/configurationTargetSlice';
import ModelSelectionPage from '../ModelSelectionPage';
import { ModelSelectionScope, ModelType } from '../../../store/global-types/customization-types';
import ModelTypeSupportCheck from '../../../components/ModelTypeSupportCheck';
import { hasBlockingFormValidationErrors } from '../../../store/global-types/store-errors';

const ContourModelSelectionPage = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);

    const customizationBaseIdsInOrder = useSelector((state: StoreState) => selectCustomizationBasesInNameOrder(state));
    const customizationBaseEntities = useSelector(contouringSelectors.selectCustomizationBaseEntities);
    const modelEntities = useSelector(contouringSelectors.selectModelEntities);
    const isAnyCustomizationModelModified = useSelector(contouringSelectors.selectIsAnyCustomizationModelModified);
    const hasFormValidationErrors = hasBlockingFormValidationErrors(useSelector(contouringSelectors.selectFormValidationErrors));

    const modelCustomizationSaveError = useSelector((state: StoreState) => state.contouring.modelCustomizationSaveError);
    const concurrentSaveError = useSelector((state: StoreState) => state.contouring.concurrentSaveError);
    const isSavingInProgress = useSelector((state: StoreState) => state.contouring.isModelCustomizationSavingInProgress);
    const customizationFetchError = useSelector((state: StoreState) => state.contouring.customizationFetchError);
    const customizationBaseIdsWithFormValidationErrors = useSelector(contouringSelectors.selectCustomizationBaseIdsWithBlockingFormValidationErrors);
    const customizationBaseIdsWithBackendValidationErrors = useSelector(contouringSelectors.selectCustomizationBaseIdsWithBackendValidationErrors);
    const tagsMappedToCustomizations = useSelector(selectVersionTagsForCustomizationBases);
    const activeVersionTags = useSelector((state: StoreState) => state.contouring.activeVersionTags);
    const isContentAvailable = currentTarget !== undefined && !!customizationBaseIdsInOrder;

    const handleUndo = useCallback(() => {
        if (!isSavingInProgress) {
            dispatch(allCustomizationChangesReverted());
            return true;
        }

        return false;
    }, [dispatch, isSavingInProgress]);

    const handleSave = useCallback((force?: boolean) => {
        if (!isSavingInProgress) {
            if (!currentTarget) {
                throw new Error('No configuration target selected, cannot save');
            }
            dispatch(modelCustomizationSaved({ configurationTarget: currentTarget, force }));
            return true;
        }

        return false;
    }, [dispatch, isSavingInProgress, currentTarget]);

    const handleSingleUndo = useCallback((scope: ModelSelectionScope, id: string) => {
        dispatch(modelSelectionChangesReverted({ revertScope: scope, id }));
        return true;
    }, [dispatch]);

    if (currentTarget === undefined) {
        return (
            <PageSection variant={PageSectionVariants.dark} padding={{ default: 'noPadding' }}>
                <EmptyState>
                    <EmptyStateHeader titleText={<>{t('selectConfigurationTarget.title')}</>} headingLevel="h4" />
                    <EmptyStateBody>{t('selectConfigurationTarget.message')}</EmptyStateBody>
                </EmptyState>
            </PageSection>
        );
    }

    return (
        <ModelTypeSupportCheck modelType={ModelType.Contouring}>
            <ModelSelectionPage
                modelType={ModelType.Contouring}
                customizationBaseIdsInOrder={customizationBaseIdsInOrder}
                customizationBaseEntities={customizationBaseEntities}
                activeVersionTags={activeVersionTags}
                modelEntities={modelEntities}
                tagsMappedToCustomizations={tagsMappedToCustomizations}
                isSavingInProgress={isSavingInProgress}
                onSave={handleSave}
                onUndo={handleUndo}
                onSingleUndo={handleSingleUndo}
                isAnyCustomizationModelModified={isAnyCustomizationModelModified}
                hasFormValidationErrors={hasFormValidationErrors}
                isContentAvailable={isContentAvailable}
                customizationFetchError={customizationFetchError}
                customizationBaseIdsWithFormValidationErrors={customizationBaseIdsWithFormValidationErrors}
                customizationBaseIdsWithBackendValidationErrors={customizationBaseIdsWithBackendValidationErrors}
                modelCustomizationSaveError={modelCustomizationSaveError}
                concurrentSaveError={concurrentSaveError}
            />
        </ModelTypeSupportCheck>
    );
};

export default ContourModelSelectionPage;
